<template>
  <div class="rounded-lg shadow bg-white p-4">
    <template v-if="!editorMode">
    <div class="flex justify-between items-center mb-4">
      <h1 class="font-bold text-xl">알림톡 관리</h1>
      <div class="flex gap-2">
        <button class="btn btn-outline-primary" @click="sync">템플릿 전체 동기화</button>
        <button class="btn btn-primary" @click="openCreate">템플릿 등록</button>
      </div>
    </div>

    <div class="flex gap-2 mb-4">
      <button class="btn" :class="tab === 'templates' ? 'btn-primary' : 'btn-outline-primary'" @click="tab = 'templates'">템플릿 관리</button>
      <button class="btn" :class="tab === 'bulk' ? 'btn-primary' : 'btn-outline-primary'" @click="tab = 'bulk'">대량 발송</button>
    </div>

    <template v-if="tab === 'templates'">
      <div class="flex gap-2 mb-3">
        <input v-model="keyword" class="form-control max-w-sm" placeholder="코드 또는 이름 검색" @keyup.enter="load">
        <select v-model="managementTypeFilter" class="form-select max-w-sm">
          <option value="">전체 타입</option>
          <option v-for="type in managementTypes" :key="type.value" :value="type.value">{{ type.label }}</option>
        </select>
        <button class="btn btn-secondary" @click="load">검색</button>
      </div>
      <div class="text-sm text-gray-500 mb-2">사용 중인 템플릿 우선, 같은 사용 여부에서는 최근 등록 순으로 표시됩니다.</div>
      <b-table :items="templates" :fields="fields" responsive hover>
        <template #cell(templateCode)="d"><button type="button" class="btn btn-link p-0 text-left" @click="openDetail(d.item)">{{ d.item.templateCode }}</button></template>
        <template #cell(templateName)="d"><button type="button" class="btn btn-link p-0 text-left" @click="openDetail(d.item)">{{ d.item.templateName }}</button></template>
        <template #cell(managementButtonName)="d"><span>{{ d.item.managementButtonName || '-' }}</span></template>
        <template #cell(templateStatusName)="d"><span class="badge bg-secondary">{{ d.item.templateStatusName || d.item.templateStatus }}</span></template>
        <template #cell(inUse)="d"><span class="badge" :class="d.item.inUse ? 'bg-success' : 'bg-secondary'">{{ d.item.inUse ? '사용 중' : '미사용' }}</span></template>
        <template #cell(actions)="d">
          <div class="flex gap-1 flex-wrap">
            <button
              class="btn btn-sm"
              :class="d.item.inUse ? 'btn-outline-danger' : 'btn-outline-success'"
              :disabled="usingId === d.item.id"
              @click.stop="toggleInUse(d.item)"
            >
              {{ usingId === d.item.id ? '처리 중' : d.item.inUse ? '사용 해제' : '사용 처리' }}
            </button>
            <button class="btn btn-sm btn-outline-secondary" :disabled="syncingCode === d.item.templateCode" @click.stop="refreshRow(d.item)">
              {{ syncingCode === d.item.templateCode ? '동기화 중' : '새로고침' }}
            </button>
            <button
              v-if="canDelete(d.item)"
              class="btn btn-sm btn-outline-danger"
              :disabled="deletingId === d.item.id"
              @click.stop="removeRow(d.item)"
            >
              {{ deletingId === d.item.id ? '삭제 중' : '삭제' }}
            </button>
          </div>
        </template>
      </b-table>
    </template>

    <template v-else>
      <div class="alert alert-warning">발송 후 취소할 수 없습니다. 대상과 치환 내용을 확인한 뒤 발송하세요.</div>
      <label class="font-semibold">승인 템플릿</label>
      <select v-model="bulkTemplateId" class="form-select mb-3">
        <option value="">선택</option>
        <option v-for="t in approved" :key="t.id" :value="t.id">{{ t.managementButtonName || t.templateName }} ({{ t.templateCode }})</option>
      </select>
      <label class="font-semibold">CSV/엑셀 파일</label>
      <input type="file" accept=".csv,.xlsx,.xls" class="form-control mb-3" @change="upload">
      <p class="text-gray-500">첫 행에 name, code, phonenumber 헤더를 넣어주세요. 최대 5,000명까지 업로드되며 1,000명씩 나누어 발송됩니다.</p>
      <div v-if="missingHeaders.length" class="alert alert-danger">엑셀에 필요한 헤더가 없습니다: <b>{{ missingHeaders.join(', ') }}</b></div>
      <div v-if="requiredParameters.length" class="alert alert-info">선택한 템플릿 치환값: {{ requiredParameters.join(', ') }}</div>
      <b-table v-if="recipients.length" :items="recipients" :fields="['row', 'phoneNumber', 'templateParameters']" responsive />
      <button class="btn btn-danger" :disabled="!bulkTemplateId || !recipients.length || missingHeaders.length > 0 || sending" @click="sendBulk">{{ recipients.length }}명 발송하기</button>
    </template>
    </template>

    <div v-else>
      <div class="flex justify-between items-center mb-4">
        <div>
          <button class="btn btn-link px-0 mb-2" @click="goList">← 템플릿 목록</button>
          <h1 class="font-bold text-xl">{{ form.id ? '템플릿 상세/수정' : '템플릿 등록' }}</h1>
        </div>
      </div>
      <div class="template-editor-grid">
        <div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="required-label">템플릿 코드</label>
              <input v-model="form.templateCode" :disabled="!!form.id" maxlength="20" class="form-control" :class="{'is-invalid': codeError}">
              <small class="text-gray-500">20자 이내 영문, 숫자, _, -만 사용 · 중복 불가</small>
              <div v-if="codeError" class="text-danger text-sm">{{ codeError }}</div>
            </div>
            <div>
              <label class="required-label">템플릿 이름</label>
              <input v-model="form.templateName" maxlength="150" class="form-control">
            </div>
            <div>
              <label class="required-label">내부 관리 타입</label>
              <select v-model="form.managementType" class="form-select">
                <option :value="null" disabled>선택해주세요</option>
                <option v-for="type in managementTypes" :key="type.value" :value="type.value">{{ type.label }}</option>
              </select>
            </div>
            <div>
              <label class="required-label">카톡 관리 버튼명</label>
              <input v-model="form.managementButtonName" maxlength="100" class="form-control" placeholder="대량발송 모달에 표시할 이름">
            </div>
            <div>
              <label class="required-label">메시지 유형</label>
              <select v-model="form.templateMessageType" class="form-select" @change="normalizeMessageType">
                <option value="BA">기본형</option>
                <option value="EX">부가 정보형</option>
                <option value="AD">채널 추가형</option>
                <option value="MI">복합형</option>
              </select>
            </div>
            <div>
              <label class="required-label">템플릿 강조 유형</label>
              <select v-model="form.templateEmphasizeType" class="form-select">
                <option value="NONE">선택 안 함</option>
                <option value="TEXT">강조 표기형</option>
              </select>
            </div>
            <div>
              <label class="required-label">보안 템플릿 여부</label>
              <select v-model="form.securityFlag" class="form-select">
                <option :value="false">미설정</option>
                <option :value="true">설정</option>
              </select>
            </div>
          </div>

          <div v-if="form.templateEmphasizeType === 'TEXT'" class="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label class="required-label">템플릿 강조 제목</label>
              <input v-model="form.templateTitle" maxlength="50" class="form-control" placeholder="최대 2줄 23자 권장">
            </div>
            <div>
              <label class="required-label">템플릿 강조 부제목</label>
              <input v-model="form.templateSubtitle" maxlength="50" class="form-control" placeholder="치환 불가, 최대 2줄 18자 권장">
            </div>
          </div>

          <div class="mt-3">
            <label class="required-label">템플릿 내용</label>
            <div class="border rounded p-3 bg-gray-50 mb-2">
              <div class="text-sm font-semibold mb-2">치환값 넣기</div>
              <div class="flex flex-wrap gap-2">
                <button v-for="v in variables" :key="v.key" type="button" class="btn btn-sm btn-outline-secondary" @click="insertVariable(v.key)">{{ v.label }} {{ variableToken(v.key) }}</button>
              </div>
            </div>
            <textarea ref="templateContent" v-model="form.templateContent" rows="12" maxlength="1300" class="form-control" placeholder="변수는 #{변수} 형태로 입력"></textarea>
          </div>

          <div v-if="needsExtra" class="mt-3">
            <label class="required-label">부가 정보</label>
            <textarea v-model="form.templateExtra" rows="3" maxlength="500" class="form-control" placeholder="치환 변수 사용 불가, URL 포함 가능, 최대 500자"></textarea>
          </div>
          <div v-if="needsChannel" class="mt-3">
            <label class="required-label">채널 추가 안내 메시지</label>
            <textarea v-model="form.templateAd" rows="3" maxlength="80" class="form-control" disabled></textarea>
            <small class="text-danger">채널 추가 버튼은 첫 번째에 자동으로 고정됩니다.</small>
          </div>

          <div class="mt-3">
            <label class="required-label">카테고리</label>
            <div class="grid grid-cols-2 gap-2">
              <select v-model="selectedCategoryName" class="form-select" @change="form.categoryCode = ''">
                <option value="">대분류 선택</option>
                <option v-for="category in categories" :key="category.name" :value="category.name">{{ category.name }}</option>
              </select>
              <select v-model="form.categoryCode" class="form-select" :disabled="!selectedCategoryName">
                <option value="">중분류 선택</option>
                <option v-for="category in selectedSubCategories" :key="category.code" :value="category.code">{{ category.name }} ({{ category.code }})</option>
              </select>
            </div>
            <div v-if="selectedCategoryInfo" class="text-sm text-gray-500 mt-1">
              <div v-if="selectedCategoryInfo.inclusion">적용 대상: {{ selectedCategoryInfo.inclusion }}</div>
              <div v-if="selectedCategoryInfo.exclusion">제외 대상: {{ selectedCategoryInfo.exclusion }}</div>
            </div>
          </div>

          <div class="mt-4">
            <div class="flex justify-between items-center mb-2">
              <div><span class="font-semibold">버튼</span><span class="text-sm text-gray-500 ml-2">최대 5개</span></div>
              <button type="button" class="btn btn-sm btn-outline-primary" :disabled="form.buttons.length >= 5" @click="addButton">버튼 추가</button>
            </div>
            <div v-for="(button, index) in form.buttons" :key="index" class="border rounded p-3 mb-2">
              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label>타입</label>
                  <select v-model="button.type" class="form-select" :disabled="needsChannel && index === 0" @change="normalizeButton(button)">
                    <option v-for="type in buttonTypes" :key="type.value" :value="type.value">{{ type.label }}</option>
                  </select>
                </div>
                <div>
                  <label class="required-label">버튼 이름</label>
                  <input v-model="button.name" maxlength="14" class="form-control" :disabled="button.type === 'AC'">
                </div>
              </div>
              <div v-if="button.type === 'WL'" class="grid grid-cols-2 gap-2 mt-2">
                <input v-model="button.linkMo" maxlength="500" class="form-control" placeholder="Mobile URL (필수)">
                <input v-model="button.linkPc" maxlength="500" class="form-control" placeholder="PC URL">
              </div>
              <div v-if="button.type === 'AL'" class="grid grid-cols-2 gap-2 mt-2">
                <input v-model="button.schemeAndroid" maxlength="500" class="form-control" placeholder="Android scheme">
                <input v-model="button.schemeIos" maxlength="500" class="form-control" placeholder="iOS scheme">
              </div>
              <div class="flex justify-end mt-2">
                <button type="button" class="btn btn-sm btn-outline-danger" :disabled="needsChannel && index === 0" @click="removeButton(index)">삭제</button>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div class="template-preview sticky top-3">
            <div class="font-semibold mb-3">치환 미리보기</div>
            <div v-if="form.templateEmphasizeType === 'TEXT'" class="border-bottom pb-2 mb-2">
              <div class="text-xs text-gray-500">강조 표기</div>
              <div class="font-bold text-lg">{{ form.templateTitle || '강조 제목' }}</div>
              <div class="text-sm">{{ form.templateSubtitle || '강조 부제목' }}</div>
            </div>
            <pre class="whitespace-pre-wrap mb-0">{{ templatePreview || '템플릿 내용을 입력해주세요.' }}</pre>
            <div v-if="needsExtra" class="text-sm text-gray-500 mt-3">{{ form.templateExtra || '부가 정보' }}</div>
            <div v-if="needsChannel" class="text-sm text-gray-500 mt-3">{{ form.templateAd }}</div>
            <div v-if="form.buttons.length" class="mt-3 grid gap-2">
              <button v-for="(button, index) in form.buttons" :key="index" type="button" class="btn btn-light border" disabled>{{ button.name || '버튼 이름' }}</button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="form.id" class="alert alert-warning mt-3 mb-0">목록의 ‘사용 처리’ 버튼으로 실제 사용 여부를 변경할 수 있습니다. 타입 1~4는 타입별 한 개만 사용할 수 있으며 NHN 승인 완료 상태여야 합니다.</div>
      <div v-if="formError" class="alert alert-danger mt-3 mb-0">{{ formError }}</div>
      <div class="flex justify-between mt-3">
        <div>
          <button
            v-if="form.id && canDelete(form)"
            class="btn btn-outline-danger"
            :disabled="deletingId === form.id"
            @click="remove"
          >
            {{ deletingId === form.id ? '삭제 중' : '삭제' }}
          </button>
        </div>
        <div class="flex gap-2">
          <button v-if="form.id" class="btn btn-outline-secondary" @click="refreshDetail">템플릿 동기화</button>
          <button v-if="form.id" class="btn btn-outline-dark" @click="saveManagement">운영 타입 저장</button>
          <button v-if="form.id" class="btn btn-outline-success" @click="openTest">테스트 발송</button>
          <button class="btn btn-primary" :disabled="!!formError" @click="save">{{ form.id ? '수정 검수 요청' : '등록 및 검수 요청' }}</button>
        </div>
      </div>
      <div v-if="form.modifications && form.modifications.length" class="mt-4">
        <h3 class="font-bold">수정 검수 이력</h3>
        <div v-for="m in form.modifications" :key="m.id" class="border rounded p-2 mt-2"><b>{{ m.templateStatusName || m.templateStatus }}</b><pre class="whitespace-pre-wrap">{{ m.templateContent }}</pre></div>
      </div>
    </div>

    <b-modal v-model="showTest" title="알림톡 테스트 발송" @ok="testSend">
      <label>수신 전화번호</label><input v-model="test.recipientNumber" class="form-control mb-2">
      <div v-for="key in form.templateParameters" :key="key" class="mb-2"><label>{{ variableToken(key) }}</label><input v-model="test.parameters[key]" class="form-control"></div>
    </b-modal>
  </div>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import api from "@/api/alimtalkTemplate";
import store from "@/store";

const CHANNEL_AD = "채널 추가하고 이 채널의 마케팅 메시지 등을 카카오톡으로 받기";
const channelButton = () => ({ordering: 1, type: "AC", name: "채널 추가"});
const emptyButton = (ordering: number) => ({ordering, type: "WL", name: "", linkMo: "", linkPc: "", schemeIos: "", schemeAndroid: ""});
const emptyForm = () => ({
  id: null, templateCode: "", managementType: null, managementButtonName: "", inUse: false, templateName: "", templateContent: "",
  templateMessageType: "BA", templateEmphasizeType: "NONE", templateExtra: "", templateAd: "",
  templateTitle: "", templateSubtitle: "", securityFlag: false, categoryCode: "", buttons: [],
  quickReplies: null, modifications: [], templateParameters: [],
});

export default defineComponent({
  name: "AlimtalkTemplates",
  data() {
    return {
      tab: "templates", keyword: "", managementTypeFilter: "",
      managementTypes: [
        {value: 1, label: "신청 후 바로 발송"}, {value: 2, label: "신청 후 1일 후 발송"},
        {value: 3, label: "어드민 신청 데이터 업로드 후 바로 발송"},
        {value: 4, label: "어드민 신청 데이터 업로드 후 1일 후 발송"}, {value: 5, label: "카톡 관리 알림톡"},
      ],
      templates: [] as any[],
      fields: [
        {key: "inUse", label: "사용 여부"}, {key: "managementTypeName", label: "타입"},
        {key: "managementButtonName", label: "카톡 노출 버튼명"},
        {key: "templateCode", label: "템플릿 코드"}, {key: "templateName", label: "템플릿 이름"},
        {key: "templateStatusName", label: "상태"}, {key: "nhnCreatedAt", label: "등록일"}, {key: "actions", label: "관리"},
      ],
      syncingCode: "", usingId: null as number | null, deletingId: null as number | null, showTest: false,
      form: emptyForm() as any, categories: [] as any[], selectedCategoryName: "",
      test: {recipientNumber: "010-1234-5678", parameters: {} as Record<string, string>},
      variables: [{key: "name", label: "이름"}, {key: "code", label: "학생코드"}, {key: "phonenumber", label: "전화번호"}],
      buttonTypes: [
        {value: "DS", label: "배송 조회"}, {value: "WL", label: "웹 링크"}, {value: "AL", label: "앱 링크"},
        {value: "BK", label: "봇 키워드"}, {value: "MD", label: "메시지 전달"},
        {value: "BC", label: "상담톡 전환"}, {value: "BT", label: "봇 전환"}, {value: "AC", label: "채널 추가"},
      ],
      bulkTemplateId: "", recipients: [] as any[], requiredParameters: [] as string[], missingHeaders: [] as string[], sending: false,
    };
  },
  computed: {
    editorMode(): boolean { return this.$route.name === "AlimtalkTemplateCreate" || this.$route.name === "AlimtalkTemplateDetail"; },
    approved(): any[] { return this.templates.filter((t: any) => t.remoteExists && this.isApproved(t)); },
    needsExtra(): boolean { return ["EX", "MI"].includes(this.form.templateMessageType); },
    needsChannel(): boolean { return ["AD", "MI"].includes(this.form.templateMessageType); },
    selectedSubCategories(): any[] { return this.categories.find((item: any) => item.name === this.selectedCategoryName)?.subCategories || []; },
    selectedCategoryInfo(): any { return this.selectedSubCategories.find((item: any) => item.code === this.form.categoryCode); },
    codeError(): string {
      return !this.form.templateCode ? "템플릿 코드를 입력해주세요." : !/^[A-Za-z0-9_-]{1,20}$/.test(this.form.templateCode) ? "영문, 숫자, _, -만 사용할 수 있습니다." : "";
    },
    formError(): string {
      if (this.codeError) return this.codeError;
      if (!this.form.templateName?.trim()) return "템플릿 이름을 입력해주세요.";
      if (!this.form.managementType) return "내부 관리 타입을 선택해주세요.";
      if (!this.form.managementButtonName?.trim()) return "카톡 관리 버튼명을 입력해주세요.";
      if (!this.form.templateContent?.trim()) return "템플릿 내용을 입력해주세요.";
      if (this.needsExtra && !this.form.templateExtra?.trim()) return "부가 정보를 입력해주세요.";
      if (this.form.templateEmphasizeType === "TEXT" && (!this.form.templateTitle?.trim() || !this.form.templateSubtitle?.trim())) return "강조 제목과 부제목을 입력해주세요.";
      if (!this.form.categoryCode) return "카테고리를 선택해주세요.";
      const invalidButton = this.form.buttons.find((button: any) => !button.name?.trim() || (button.type === "WL" && !button.linkMo?.trim()));
      if (invalidButton) return "버튼 이름과 필수 링크를 입력해주세요.";
      return "";
    },
    templatePreview(): string {
      return (this.form.templateContent || "").replace(/#\{name}/g, "홍길동").replace(/#\{code}/g, "ks00000001").replace(/(?:#|\$)\{phonenumber}/g, "010-1234-5678");
    },
  },
  mounted() { this.load(); this.loadCategories(); this.loadEditorRoute(); },
  watch: { "$route.fullPath"() { this.loadEditorRoute(); } },
  methods: {
    ok(r) { return r.data.data; },
    fail(e) { alert(e?.response?.data?.message || e?.message || "요청에 실패했습니다."); },
    isApproved(template) { return template.templateStatus === "APR" || template.templateStatus === "TSC03" || (template.templateStatusName || "").includes("승인"); },
    load() { api.list({keyword: this.keyword, managementType: this.managementTypeFilter}, r => this.templates = this.ok(r), this.fail); },
    loadCategories() { api.categories(r => { this.categories = this.ok(r) || []; if (this.editorMode) this.resolveCategory(); }, this.fail); },
    sync() { api.sync(r => { this.load(); alert(`${this.ok(r).syncedCount}건 동기화했습니다.`); }, this.fail); },
    refreshRow(row) { this.syncingCode = row.templateCode; api.syncDetail(row.templateCode, () => { this.syncingCode = ""; this.load(); }, e => { this.syncingCode = ""; this.fail(e); }); },
    canSetInUse(row) { return !!row.managementType && !!row.managementButtonName?.trim() && row.remoteExists && this.isApproved(row); },
    canDelete(template) { return template.managementType == null || Number(template.managementType) === 5; },
    toggleInUse(row) {
      if (!row.inUse && !this.canSetInUse(row)) {
        const reasons: string[] = [];
        if (!row.managementType) reasons.push("내부 관리 타입 미지정");
        if (!row.managementButtonName?.trim()) reasons.push("카톡 관리 버튼명 미입력");
        if (!row.remoteExists) reasons.push("NHN 원격 템플릿 없음");
        if (!this.isApproved(row)) reasons.push("NHN 검수 미승인");
        alert(`사용 처리할 수 없습니다.\n- ${reasons.join("\n- ")}`); return;
      }
      const existing = this.templates.find((t: any) => t.inUse && t.managementType === row.managementType && t.id !== row.id);
      let message = row.inUse ? `[${row.templateName}]을 사용 해제할까요?` : `[${row.templateName}]을 사용 처리할까요?`;
      if (!row.inUse && row.managementType >= 1 && row.managementType <= 4 && existing) message = `[${existing.templateName}]을 미사용 처리하고\n[${row.templateName}]으로 교체합니다. 계속할까요?`;
      if (!confirm(message)) return;
      this.usingId = row.id;
      api.updateManagement(row.id, {managementType: row.managementType, managementButtonName: row.managementButtonName, inUse: !row.inUse}, r => { this.usingId = null; this.load(); const d = this.ok(r); alert(d.replacedTemplateName ? `[${d.replacedTemplateName}]에서 [${d.template.templateName}]으로 교체했습니다.` : "사용 여부를 변경했습니다."); }, e => { this.usingId = null; this.fail(e); });
    },
    openCreate() { this.$router.push({name: "AlimtalkTemplateCreate"}); },
    openDetail(row) { this.$router.push({name: "AlimtalkTemplateDetail", params: {id: row.id}}); },
    goList() { this.$router.push({name: "AlimtalkTemplates"}); },
    loadEditorRoute() {
      if (this.$route.name === "AlimtalkTemplateCreate") { this.form = emptyForm(); this.selectedCategoryName = ""; return; }
      if (this.$route.name === "AlimtalkTemplateDetail") api.detail(this.$route.params.id, r => { this.form = this.normalizeForm(this.ok(r)); this.resolveCategory(); }, this.fail);
    },
    normalizeForm(form) { return {...emptyForm(), ...form, buttons: Array.isArray(form.buttons) ? form.buttons.map((button: any, index: number) => ({...emptyButton(index + 1), ...button})) : []}; },
    resolveCategory() { const category = this.categories.find((item: any) => (item.subCategories || []).some((sub: any) => sub.code === this.form.categoryCode)); this.selectedCategoryName = category?.name || ""; },
    refreshDetail() { api.syncDetail(this.form.templateCode, r => { this.form = this.normalizeForm(this.ok(r)); this.resolveCategory(); this.load(); alert("템플릿 최신 상태를 가져왔습니다."); }, this.fail); },
    saveManagement() {
      if (!this.form.managementType || !this.form.managementButtonName?.trim()) { alert("내부 관리 타입과 카톡 관리 버튼명을 모두 입력해주세요."); return; }
      api.updateManagement(this.form.id, {managementType: this.form.managementType, managementButtonName: this.form.managementButtonName.trim(), inUse: this.form.inUse}, r => { this.form = this.normalizeForm(this.ok(r).template); this.load(); alert("운영 타입을 저장했습니다."); }, this.fail);
    },
    normalizeMessageType() {
      if (this.needsChannel) { this.form.templateAd = CHANNEL_AD; const withoutChannel = this.form.buttons.filter((button: any) => button.type !== "AC"); this.form.buttons = [channelButton(), ...withoutChannel].slice(0, 5); }
      else { this.form.templateAd = ""; this.form.buttons = this.form.buttons.filter((button: any) => button.type !== "AC"); }
      this.reorderButtons();
    },
    addButton() { if (this.form.buttons.length < 5) this.form.buttons.push(emptyButton(this.form.buttons.length + 1)); },
    removeButton(index) { this.form.buttons.splice(index, 1); this.reorderButtons(); },
    reorderButtons() { this.form.buttons.forEach((button: any, index: number) => button.ordering = index + 1); },
    normalizeButton(button) { Object.assign(button, emptyButton(button.ordering), {type: button.type}); if (button.type === "AC") button.name = "채널 추가"; },
    openTest() { const defaults = {name: "홍길동", code: "ks00000001", phonenumber: "010-1234-5678"}; this.test.parameters = {}; (this.form.templateParameters || []).forEach(key => this.test.parameters[key] = defaults[key] || ""); this.showTest = true; },
    variableToken(key) { return `#{${key}}`; },
    insertVariable(key) { const token = this.variableToken(key); const el = this.$refs.templateContent as HTMLTextAreaElement; if (!el) { this.form.templateContent += token; return; } const start = el.selectionStart, end = el.selectionEnd; this.form.templateContent = this.form.templateContent.slice(0, start) + token + this.form.templateContent.slice(end); this.$nextTick(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); }); },
    save() { if (this.formError) { alert(this.formError); return; } this.reorderButtons(); const payload = {...this.form, managementButtonName: this.form.managementButtonName.trim(), buttons: this.form.buttons}; const call = this.form.id ? api.update.bind(api, this.form.id) : api.create.bind(api); call(payload, () => this.goList(), this.fail); },
    openDeleteModal(template, onDeleted) {
      store.dispatch("modal/openModal", {
        type: "warning",
        title: "알림톡 템플릿 삭제",
        html: `<p class="font-semibold text-rose-600">삭제 후에는 복구할 수 없습니다.</p>
          <p class="mt-2">NHN Cloud에서도 함께 삭제됩니다. 다시 사용하려면 템플릿을 새로 등록하고 검수를 받아야 합니다.</p>
          <p class="text-sm text-gray-500 mt-3 mb-0">※ 승인된 템플릿은 최근 3일간 발송 이력이 없어야 삭제할 수 있습니다.</p>`,
        confirm: () => {
          this.deletingId = template.id;
          api.delete(template.id, () => {
            this.deletingId = null;
            onDeleted();
          }, e => {
            this.deletingId = null;
            this.fail(e);
          });
        },
      });
    },
    remove() { this.openDeleteModal(this.form, () => this.goList()); },
    removeRow(row) { this.openDeleteModal(row, () => this.load()); },
    testSend() { api.testSend(this.form.id, {phoneNumber: this.test.recipientNumber, templateParameters: this.test.parameters}, r => alert(this.ok(r).success ? "발송 요청 성공" : "발송 실패: " + this.ok(r).errorMessage), this.fail); },
    upload(e) { const file = e.target.files[0]; if (!this.bulkTemplateId) { alert("템플릿을 먼저 선택해주세요."); e.target.value = ""; return; } if (file) api.preview(this.bulkTemplateId, file, r => { const d = this.ok(r); this.recipients = d.recipients; this.requiredParameters = d.requiredParameters; this.missingHeaders = d.missingHeaders; }, this.fail); },
    sendBulk() { if (!confirm(`${this.recipients.length}명에게 발송합니다. 계속할까요?`)) return; this.sending = true; api.bulkSend(this.bulkTemplateId, this.recipients, r => { this.sending = false; const d = this.ok(r); alert(`${d.batchCount}회 분할 발송 완료\n성공 ${d.succeeded}건 / 실패 ${d.failed}건`); }, e => { this.sending = false; this.fail(e); }); },
  },
});
</script>

<style scoped>
.template-editor-grid { display: grid; grid-template-columns: minmax(0, 3fr) minmax(280px, 2fr); gap: 20px; }
.template-preview { min-height: 420px; padding: 20px; border: 1px solid #d8dee6; border-radius: 12px; background: #eef5fb; }
.required-label::after { content: " *"; color: #dc3545; }
@media (max-width: 992px) { .template-editor-grid { grid-template-columns: 1fr; } }
</style>
